import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../App.css';
import './Matching.css';
import './ScoreAdjustments.css';
import './ReasonsOverride.css';
import api from '../../utils/api.js';
import MatchingFilterForm from './MatchingFilterForm';
import RecommendationCard from './RecommendationCard';
import JobDetailModal from './JobDetailModal';
import PdfExportButton from './PdfExportButton';
import JobUploadZone from './JobUploadZone';
import JobSelectionList from './JobSelectionList';
import ResultExecutedAt from './ResultExecutedAt';
import ReasonsNotGeneratedNotice from './ReasonsNotGeneratedNotice';
import CandidateVisibleCount from './CandidateVisibleCount';
import { countCandidateVisible, isVisibleToCandidate } from './candidateVisibility';
import { canDeleteJobPoolItem, displayRanks, isMissingFromJobPool } from './jobPoolDeletion';
import { hasRecommendedMark } from './jobPoolScope';
import useJobPoolDelete from './useJobPoolDelete';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext.js';
import './JobPoolDelete.css';
import useAiMatching from './useAiMatching';
import { reorderStatusText } from './useReorderSave';
import { hasEnoughPoints, QUOTA_OPERATION } from './aiQuota';
import { useJobUploadCompleted, useJobUploadConfirmSettled } from '../../contexts/JobUploadContext.js';
import { fetchResumeAttachments } from '../../services/resumeApi.js';

/**
 * AI マッチング画面 (SCR-M-001, T-002-U1)
 *
 * 案件詳細「提案した求人」タブ (SCR-E-004) から遷移。画面レイアウト_SCR-M-001.md §6.1 準拠で、
 * 推薦結果の有無にかかわらずドロップゾーン (`JobUploadZone`, T-001-U1) + マッチングの
 * 対象にする求人の一覧 (`JobSelectionList`, §4.6、Issue #779) を表示し、その場で求人取込から
 * 対象の選択、AI マッチング実行まで行える (推薦結果の表示中も表示する。Issue #707)。
 */
function RecommendationList() {
    const { opportunityId } = useParams();
    const navigate = useNavigate();

    const { currentUser } = useAuth();
    const [opportunity, setOpportunity] = useState(null);
    const [detailJob, setDetailJob] = useState(null);
    // PDF の出力中 (求人プールからの削除を押せなくするため。BTN-M-014、Issue #883)
    const [pdfExporting, setPdfExporting] = useState(false);
    // AIマッチング実行前バリデーション用(Issue #591)。null=未取得のため判定を保留する。
    const [meetingsTotal, setMeetingsTotal] = useState(null);
    // 同じく事前バリデーション用の履歴書一覧(Issue #1057)。null=未取得・取得失敗のため判定を保留する。
    const [resumeAttachments, setResumeAttachments] = useState(null);

    const {
        filters,
        updateFilters,
        jobPoolItems,
        jobPoolCount,
        visibleJobPoolItems,
        jobPoolScope,
        changeJobPoolScope,
        jobPoolScopeStatus,
        jobPoolLoading,
        jobPoolError,
        selectedIdSet,
        selectedCount,
        selectionState,
        selectionDiffersFromResult,
        toggleJobPoolItem,
        toggleSelectAll,
        selectImportedJobPoolItem,
        removeJobPoolItem,
        quota,
        quotaLoading,
        refreshQuota,
        phase,
        errorType,
        errorMessage,
        matchingResult,
        orderedItems,
        runMatching,
        moveItem,
        moveItemToIndex,
        isReorderDirty,
        reorderSaving,
        reorderError,
        reorderRetryable,
        reorderJustSaved,
        retryReorderSave,
        flushReorderSave,
        scoreHidden,
        scoreVisibleChecked,
        scoreVisibilitySaving,
        scoreVisibilityError,
        changeScoreHidden,
        scoreOverrideSaving,
        editingScoreItemId,
        savingScoreItemId,
        scoreOverrideError,
        startScoreEdit,
        cancelScoreEdit,
        clearScoreOverrideError,
        saveScore,
        candidateVisibilitySaving,
        candidateVisibilityPending,
        candidateVisibilityError,
        changeCandidateVisible,
        reasonsOverrideSaving,
        editingReasonsItemId,
        savingReasonsItemId,
        reasonsOverrideError,
        startReasonsEdit,
        cancelReasonsEdit,
        clearReasonsOverrideError,
        saveReasons,
    } = useAiMatching(opportunityId, opportunity, meetingsTotal, resumeAttachments, currentUser?.id);

    // 求人プールからの削除 (BTN-M-014。SCR-M-001 §6.2「求人プールからの削除（BTN-M-014）」、Issue #883)
    const {
        deleteTarget,
        jobPoolDeleting,
        jobPoolDeleteError,
        jobPoolDeleteStatus,
        deleteFocusRequest,
        openDelete,
        cancelDelete,
        confirmDelete,
        clearFocusRequest,
    } = useJobPoolDelete(visibleJobPoolItems, removeJobPoolItem);

    // マッチ度の保存中 (表示の設定・数字のどちらでも) と、どれかのカードの数字を編集している間は、
    // 「マッチ度を表示する」と PDF 出力を押せなくする (保存・編集の途中の状態で設定が変わったり
    // PDF が作られたりしないようにするため。SCR-M-001 §6.2、Issue #1435)。
    // 求職者に提案するかの保存中も同じく押せなくする (同じ推薦結果の行をロックして書くため・保存の途中の
    // 設定で PDF が作られないため。§6.2「同時に保存するもの」・BTN-M-003 ⑤、Issue #992)。
    // 求人プールからの削除中も押せなくする (削除の途中で、消える求人を含む操作を始めさせない。§6.2 手順 2、Issue #883)
    // おすすめポイントの文の保存中も同じく押せなくする (同じ推薦結果の行をロックして書くため。§6.2
    // 「同時に保存するもの」、Issue #777)。文の編集中は、PDF 出力と削除だけを押せなくする (IN-M-009・IN-M-011 は
    // 文の入力欄を消さないため押せる。§6.2「おすすめポイントの文の編集」手順 2・BTN-M-003 ⑦)
    // 並べ替えの保存中も同じく押せなくする (並べ替えの保存がマッチ度も書くようになったため。§6.2
    // 「並べ替えの保存でマッチ度を付け直す」手順 3・BTN-M-003 ⑧、Issue #2173)。保存中は、動かしてから
    // 応答を受け取るまで (待っている間を含む。Issue #2462)
    const scoreBusy = scoreVisibilitySaving || scoreOverrideSaving || candidateVisibilitySaving
        || reasonsOverrideSaving || jobPoolDeleting || reorderSaving;
    const scoreControlsLocked = scoreBusy || editingScoreItemId !== null;
    const reasonsEditing = editingReasonsItemId !== null;
    // 編集できるのは画面全体で 1 か所 (マッチ度の数字かおすすめポイントの文の、どちらか 1 枚。§6.2 手順 1)
    const anyEditing = editingScoreItemId !== null || reasonsEditing;

    // 求人プールに無い求人の枠 (SCR-M-001 §4.4、Issue #883) を出すかは、一覧の取得に成功しているときだけ決める
    // (取得に失敗している間は、どの求人が消えたかを判断できない)
    const jobPoolLoaded = !jobPoolLoading && !jobPoolError;
    const jobPoolIdSet = new Set(jobPoolItems.map((item) => item.id));
    const isInJobPool = (rec) => !jobPoolLoaded || jobPoolIdSet.has(rec.job_pool_item_id);

    // DP-M-017 の件数。保存済みの設定で数える (保存中の切り替えは、応答を受け取るまで数えない。§6.2 手順 2)。
    // 枠の求人は N にも M にも数えない (PM の判断 2026-10-06。#883 comment 6007947574 の C)
    const candidateCount = countCandidateVisible(matchingResult?.recommendations, isInJobPool);
    // BTN-M-003 ⑥: 載せる求人が 1 件以上 (Issue #992)。推薦候補がすべて枠 (M = 0) でも押せない (Issue #883)。
    // 推薦の要素が無い結果 (応答に recommendations が無い) では判定しない (改める前と同じ)
    const recommendationTotal = matchingResult?.recommendations?.length ?? 0;
    const noCandidateProposed = recommendationTotal > 0 && candidateCount.visible === 0;
    // 画面に出す順位。枠を数えずに振る (§4.4「順位の番号」。推薦 PDF と同じ番号)
    const ranks = displayRanks(orderedItems, jobPoolLoaded);

    // BTN-M-014 を出すか (求人ごと。マネージャー・取り込んだ本人・開いている案件の担当者。§6.2「BTN-M-014 を
    // 出す条件」、Issue #782) と、出していても押せない間 (削除中・一覧の取得中・マッチングの実行中・PDF の出力中・
    // 並べ替えの保存中・マッチ度の保存中と編集中・求職者に提案するかの保存中。§4.3)
    const canDeleteItem = (item) => canDeleteJobPoolItem(currentUser, opportunity, opportunityId, item);
    // 「おすすめ」の印 (DP-M-020。§4.6.6 #3、Issue #782)。表示のたびに、ログイン中の利用者で判定する
    const isRecommended = (item) => hasRecommendedMark(item, opportunityId, currentUser?.id);
    const deleteDisabled =
        jobPoolDeleting || jobPoolLoading || phase === 'loading' || pdfExporting || reorderSaving || scoreControlsLocked
        || reasonsEditing;

    // カード 1 枚に渡す、求職者に提案するかの状態と操作 (IN-M-011。RecommendationCard の props)。
    // 数字の編集中は押せる (編集中の欄は IN-M-011 で消えないため。§6.2「同時に保存するもの」)
    const buildCandidateVisibility = (item) => {
        const visible = isVisibleToCandidate(item);
        const pendingHere = candidateVisibilityPending?.itemId === item.job_pool_item_id;
        return {
            visible,
            checked: pendingHere ? candidateVisibilityPending.visible : visible,
            saving: pendingHere,
            locked: candidateVisibilitySaving || scoreVisibilitySaving || scoreOverrideSaving || reasonsOverrideSaving
                || jobPoolDeleting || reorderSaving,
            errorMessage:
                candidateVisibilityError?.itemId === item.job_pool_item_id ? candidateVisibilityError.message : null,
            onChange: (nextVisible) => changeCandidateVisible(item.job_pool_item_id, nextVisible),
        };
    };

    // カード 1 枚に渡す、マッチ度の編集の状態と操作 (RecommendationScore の props)
    const buildScoreEdit = (jobPoolItemId) => ({
        editing: editingScoreItemId === jobPoolItemId,
        saving: savingScoreItemId === jobPoolItemId,
        // 編集できるカードは同時に 1 枚。ほかのカードを編集中・マッチ度の保存中は押せない。
        // おすすめポイントの文の編集中も押せない (編集は画面全体で 1 か所。Issue #777)
        locked: scoreBusy || reasonsEditing || (editingScoreItemId !== null && editingScoreItemId !== jobPoolItemId),
        errorMessage: scoreOverrideError?.itemId === jobPoolItemId ? scoreOverrideError.message : null,
        onStartEdit: () => startScoreEdit(jobPoolItemId),
        onCancelEdit: cancelScoreEdit,
        onSave: (score) => saveScore(jobPoolItemId, score),
        onClearError: clearScoreOverrideError,
    });

    // カード 1 枚に渡す、おすすめポイントの文の編集の状態と操作 (RecommendationReasons の props。Issue #777)
    const buildReasonsEdit = (jobPoolItemId) => ({
        editing: editingReasonsItemId === jobPoolItemId,
        saving: savingReasonsItemId === jobPoolItemId,
        // 「編集」「AI の文に戻す」: どこかを編集中 (このカードの文の編集中は部品が出し分ける)・どれかの保存中は押せない
        locked: scoreBusy || anyEditing,
        // 編集中の「保存」: ほかの保存中は押せない (BTN-M-016。IN-M-009・IN-M-011 は文の編集中も押せるため)
        saveLocked: scoreBusy && savingReasonsItemId !== jobPoolItemId,
        errorMessage: reasonsOverrideError?.itemId === jobPoolItemId ? reasonsOverrideError.message : null,
        onStartEdit: () => startReasonsEdit(jobPoolItemId),
        onCancelEdit: cancelReasonsEdit,
        onSave: (text) => saveReasons(jobPoolItemId, text),
        onClearError: clearReasonsOverrideError,
    });

    // D&D 並べ替え (Issue #596) のドラッグ中状態。ドラッグ元 ID は再レンダリング不要のため ref、
    // ドロップ先候補 index は視覚フィードバック (recommendation-card--drag-over) に使うため state。
    const draggedItemIdRef = useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleCardDragStart = (jobPoolItemId) => {
        draggedItemIdRef.current = jobPoolItemId;
    };

    const handleCardDragOver = (index) => {
        setDragOverIndex(index);
    };

    const handleCardDrop = (index) => {
        if (draggedItemIdRef.current) {
            moveItemToIndex(draggedItemIdRef.current, index);
        }
        draggedItemIdRef.current = null;
        setDragOverIndex(null);
    };

    const handleCardDragEnd = () => {
        draggedItemIdRef.current = null;
        setDragOverIndex(null);
    };

    const handleUploadComplete = (jobPoolItem) => {
        // 取り込んだ求人を選択に加えて一覧を取り直す (§6.2 手順 4・5、Issue #779)
        selectImportedJobPoolItem(jobPoolItem);
    };
    // 取込は JobUploadZone の表示有無に関わらず続くため、完了の通知は画面側で受ける (Issue #759)
    useJobUploadCompleted(opportunityId, handleUploadComplete);
    // 取込確定はポイントを消費しうるため、結果が決まるたびに残量を取り直す
    // (完了・エラー・通信の失敗を問わず、どの案件のファイルでも。§6.4.4「取り直す時機」③、Issue #882)
    useJobUploadConfirmSettled(refreshQuota);

    // AI ポイントの残量による活性判定 (§6.4.4)。必要なポイントは残量 API の応答から取る
    // PDF 出力 (BTN-M-003) は判定しない。AI ポイントを消費しないため残量によらず押せる (Issue #1208)
    const canImportJobFile = hasEnoughPoints(quota, QUOTA_OPERATION.jobPoolExtraction);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const response = await api.fetch(`/opportunities/${opportunityId}`);
            if (!response || !response.ok || cancelled) return;
            const data = await response.json();
            if (!cancelled) setOpportunity(data);
        })();
        return () => { cancelled = true; };
    }, [opportunityId]);

    // 面談履歴の有無をAIマッチング実行前バリデーション用に取得する(Issue #591)。
    // 一覧内容は不要なため件数(total)のみを軽量に取得する。
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const response = await api.fetch(`/opportunities/${opportunityId}/meetings?page=1&per_page=1`);
            if (!response || !response.ok || cancelled) return;
            const data = await response.json();
            if (!cancelled) setMeetingsTotal(data.total ?? 0);
        })();
        return () => { cancelled = true; };
    }, [opportunityId]);

    // 読み取れた履歴書の有無をAIマッチング実行前バリデーション用に取得する(Issue #1057)。
    // 取得に失敗した場合は null のままにし、判定を保留する(誤って実行をブロックしない)。
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const result = await fetchResumeAttachments(opportunityId);
            if (!result.ok || cancelled) return;
            setResumeAttachments(result.attachments);
        })();
        return () => { cancelled = true; };
    }, [opportunityId]);

    // 「戻る」(BTN-M-001)。待っている並べ替えの保存は、待たずに送ってから画面を移る (応答は待たない。
    // 画面を移った後も要求は続く。Issue #2462)
    const handleBack = () => {
        flushReorderSave();
        navigate(`/opportunity/${opportunityId}?tab=proposed_jobs`);
    };

    return (
        // tablet-ready-page: タブレット幅で body の min-width を外す (index.css、SCR-M-001 §3.2、Issue #1267)
        <div className="detail-container tablet-ready-page">
            <div className="detail-header">
                <div className="header-left">
                    <h1 className="page-title">AI マッチング</h1>
                    {opportunity && (
                        <p className="matching-candidate-name">対象求職者: {opportunity.applicant_name}</p>
                    )}
                </div>
                <div className="header-actions">
                    <button className="modal-button-cancel" onClick={handleBack}>
                        ← 戻る
                    </button>
                </div>
            </div>

            {/* DP-M-007: Agent-in-the-loop 明示 (職業安定法対応) */}
            <div className="matching-compliance-banner" role="note">
                本結果は AI 推薦です。最終判断は担当者が行います（職業安定法対応）。
            </div>

            <div className="detail-card">
                {/* 推薦結果の表示中も取込 UI と一覧を出す (§4.6.2「画面内の並び」・§6.1、Issue #707) */}
                <JobUploadZone targetCandidateId={opportunityId} canImport={canImportJobFile} />

                {/* DP-M-013: 領域ごと出し入れすると読み上げられないことがあるため、
                    role="status" の領域は常に置き、中の案内だけを出し入れする (§4.6.4) */}
                <div role="status" className="matching-selection-differs-status">
                    {/* 「全部」から「だけ」に戻して選択を外したとき (§4.6.4、Issue #782) */}
                    {jobPoolScopeStatus && (
                        <p className="matching-selection-scope-status">{jobPoolScopeStatus}</p>
                    )}
                    {selectionDiffersFromResult && (
                        <p className="matching-selection-differs-notice">
                            選択が、表示中の推薦結果と異なります。『AI マッチングを実行』を押すと、選択した求人で推薦し直します。
                        </p>
                    )}
                </div>

                <JobSelectionList
                    items={visibleJobPoolItems}
                    loading={jobPoolLoading}
                    errorMessage={jobPoolError}
                    selectedIdSet={selectedIdSet}
                    selectedCount={selectedCount}
                    selectionState={selectionState}
                    onToggleItem={toggleJobPoolItem}
                    onToggleAll={toggleSelectAll}
                    scope={{ value: jobPoolScope, onChange: changeJobPoolScope, totalCount: jobPoolCount }}
                    isRecommended={isRecommended}
                    deletion={{
                        canDeleteItem,
                        disabled: deleteDisabled,
                        onDelete: openDelete,
                        statusMessage: jobPoolDeleteStatus,
                        focusRequest: deleteFocusRequest,
                        onFocusHandled: clearFocusRequest,
                    }}
                />

                <MatchingFilterForm
                    filters={filters}
                    onChange={updateFilters}
                    onSubmit={runMatching}
                    submitting={phase === 'loading'}
                    // 並べ替えの保存中 (待っている間を含む) は押せない。動かした並びが保存されないまま、表示する
                    // 推薦結果が替わらないようにするため (BTN-M-002、Issue #2462)
                    submitDisabled={
                        jobPoolLoading || jobPoolCount === 0 || selectedCount === 0 || jobPoolDeleting || reorderSaving
                    }
                    quota={quota}
                    quotaLoading={quotaLoading}
                    selectedCount={selectedCount}
                    showNoSelectionError={
                        // DP-M-021 (「だけ」が 0 件の案内) を出しているときは出さない (§5.2 No.4、Issue #782)
                        !jobPoolLoading && !jobPoolError && visibleJobPoolItems.length > 0 && selectedCount === 0
                    }
                />

                {/* §5.2 No.3。取得に失敗したときは出さない (取得できなかったことを「登録されていない」に見せない) */}
                {jobPoolCount === 0 && !jobPoolLoading && !jobPoolError && (
                    <p className="no-data">
                        求人プールに求人が登録されていません。求人取込を行ってからマッチングを実行してください。
                    </p>
                )}

                <div aria-live="polite">
                    {phase === 'loading' && (
                        <div className="loading-message">AI マッチングを実行中です...</div>
                    )}

                    {phase === 'error' && (
                        <div className="matching-error-banner" role="alert">
                            <p>{errorMessage}</p>
                            {errorType === 'quota' && (
                                <p className="matching-filter-empty">管理者にAIクレジット枠の拡張をご依頼ください。</p>
                            )}
                            {(errorType === 'server' || errorType === 'network') && (
                                /* 「ポイント未消費」とは書かない。Phase 2 の失敗（未消費）と
                                   Phase 3 の失敗（消費済み）がどちらも同じ 500 で返るため、
                                   画面は消費の有無を知れない（SCR-M-001 §6.3 の注記 ②。Issue #868）。
                                   通信の失敗（network）にも出す——要求が届いて Phase 2 まで進んで
                                   いる場合があり、5xx との違いは応答が返ったかどうかだけ（同 No.2） */
                                <p className="matching-filter-empty">
                                    再試行すると AI ポイントを消費する場合があります。
                                </p>
                            )}
                            {(errorType === 'server' || errorType === 'network') && (
                                /* 手動の再試行。自動リトライはしない（同表の注記 ①）。並べ替えの保存中は押せない
                                   (BTN-M-007、Issue #2462) */
                                <button
                                    type="button"
                                    className="modal-button-cancel"
                                    onClick={runMatching}
                                    disabled={reorderSaving}
                                >
                                    再試行
                                </button>
                            )}
                            {/* client: 4xx・401。サーバが要求を受け付けないと判断しており、
                                やり直しても同じ結果になるため再試行を出さない（同表 No.1・No.4）
                                validation: 求職者情報未入力等の事前バリデーションエラー(Issue #591)。
                                API呼出前の判定のため再試行しても状況は変わらず、案内のみ表示する */}
                        </div>
                    )}

                    {/* DP-M-015: 推薦の実行日時。推薦結果 (0 件の案内を含む) の直上 (SCR-M-001 §4.2・§4.6.2、Issue #710) */}
                    {(phase === 'results' || phase === 'empty') && matchingResult && (
                        <ResultExecutedAt createdAt={matchingResult.created_at} />
                    )}

                    {phase === 'empty' && (
                        <p className="no-data">
                            選択した求人の中に、条件に合う求人が見つかりませんでした。年収の条件を外すか、選択する求人を増やしてください。
                        </p>
                    )}

                    {phase === 'results' && (
                        <>
                            {/* DP-M-016: おすすめポイントを生成できなかった注記。実行日時の直下、操作の行の上
                                (SCR-M-001、Issue #1129)。項目が無い・true のときは出さない (=== false で判定)。
                                候補 0 件 (phase=empty) では出さない (0 件の案内が出る場面)。
                                推薦候補がすべて求人プールに無い求人の枠 (DP-M-017 の M = 0) でも出さない。削除で文が外れると
                                生成に成功した結果でも false になるため、説明は DP-M-017 の文 1 つにする (§4.2 No.16、Issue #1920) */}
                            {matchingResult && matchingResult.reasons_generated === false && candidateCount.total > 0 && (
                                <ReasonsNotGeneratedNotice />
                            )}

                            {/* 並べ替えの横の文 (職業安定法 Agent-in-the-loop の実施証跡の保存の状態)。並べ替えは
                                動かすたびに自動で保存し、同じ 1 回の保存でマッチ度を上から高い順に付け直す
                                (Issue #2173・#2462。「並べ替えを保存」〈BTN-M-004〉は #2462 で廃止)。
                                失敗したときは失敗の文言と、押し直せる失敗だけ「保存し直す」(BTN-M-019) を出す */}
                            <div className="matching-reorder-save-bar">
                                {reorderError && (
                                    <span className="matching-reorder-error" role="alert">{reorderError}</span>
                                )}
                                {reorderError && reorderRetryable && (
                                    <button
                                        type="button"
                                        className="btn-row btn-row--ghost"
                                        onClick={retryReorderSave}
                                        disabled={reorderSaving}
                                    >
                                        保存し直す
                                    </button>
                                )}
                                {/* 横の文 #1・#3・#4。読み上げのため role="status" は常に置く */}
                                <span className="matching-reorder-saved" role="status">
                                    {reorderStatusText({
                                        saving: reorderSaving,
                                        failed: Boolean(reorderError),
                                        justSaved: reorderJustSaved,
                                    })}
                                </span>

                                {/* IN-M-009: マッチ度を表示する (Issue #1435)。切り替えた時点で保存し、画面と
                                    推薦 PDF の両方に効く。保存中は切り替えた後の状態で表示して押せなくする
                                    (SCR-M-001 §6.2「マッチ度の表示・非表示の切り替え」) */}
                                <div className="matching-score-visibility">
                                    <label className="matching-score-visibility-label">
                                        <input
                                            type="checkbox"
                                            checked={scoreVisibleChecked}
                                            onChange={(event) => changeScoreHidden(!event.target.checked)}
                                            disabled={scoreControlsLocked}
                                        />
                                        マッチ度を表示する
                                    </label>
                                    {/* 領域ごと出し入れすると読み上げられないことがあるため、role="status" は常に置く */}
                                    <span role="status" className="matching-score-visibility-status">
                                        {scoreVisibilitySaving && '保存中...'}
                                    </span>
                                    {scoreVisibilityError && (
                                        <span className="matching-score-visibility-error" role="alert">
                                            {scoreVisibilityError}
                                        </span>
                                    )}
                                </div>

                                {/* BTN-M-003: PDF エクスポート (推薦結果あり時のみ表示、Issue #470)。
                                    AI ポイントの残量によらず押せる (Issue #1208)。
                                    PDF は表示中の結果から作る。並べ替えの保存と同じ matchingResult.id を渡す
                                    (phase が results の間は matchingResult がある。Issue #878)。
                                    マッチ度の保存中と、マッチ度の数字の編集中は押せない (保存・編集の途中の状態で
                                    PDF が作られないようにするため。BTN-M-003 ③④、Issue #1435)。
                                    おすすめポイントの文の編集中も押せない (BTN-M-003 ⑦、Issue #777)。
                                    PDF はサーバが保存済みの並びで作るため、画面の並びが保存済みの並びと違う間
                                    (保存を待っている間・保存中・保存が失敗した後) も押せない (BTN-M-003 ⑧、Issue #2462) */}
                                <PdfExportButton
                                    opportunityId={opportunityId}
                                    matchingResultId={matchingResult.id}
                                    onExportFailure={refreshQuota}
                                    onExportingChange={setPdfExporting}
                                    disabled={scoreControlsLocked || reasonsEditing || noCandidateProposed || isReorderDirty}
                                />
                            </div>

                            {/* DP-M-017: 求職者に提案する求人の件数。操作の行の直下に常に出す。載せる求人が 0 件のときは
                                BTN-M-003 を押せない理由 (§5.2 No.7) を出す (SCR-M-001 §4.2、Issue #992) */}
                            <CandidateVisibleCount visible={candidateCount.visible} total={candidateCount.total} />

                            <div className="recommendation-list">
                                {orderedItems.map((item, index) => (
                                    <RecommendationCard
                                        key={item.job_pool_item_id}
                                        item={item}
                                        index={index}
                                        isFirst={index === 0}
                                        isLast={index === orderedItems.length - 1}
                                        onMoveUp={(id) => moveItem(id, 'up')}
                                        onMoveDown={(id) => moveItem(id, 'down')}
                                        onOpenDetail={setDetailJob}
                                        onDragStart={handleCardDragStart}
                                        onDragOverCard={handleCardDragOver}
                                        onDropCard={handleCardDrop}
                                        onDragEnd={handleCardDragEnd}
                                        isDragOver={dragOverIndex === index}
                                        // マッチ度の数字の編集中は、どのカードも動かせない (Issue #2462)
                                        reorderLocked={editingScoreItemId !== null}
                                        scoreHidden={scoreHidden}
                                        scoreEdit={buildScoreEdit(item.job_pool_item_id)}
                                        reasonsEdit={buildReasonsEdit(item.job_pool_item_id)}
                                        candidateVisibility={buildCandidateVisibility(item)}
                                        missingFromPool={isMissingFromJobPool(item, jobPoolLoaded)}
                                        displayRank={ranks[index]}
                                        recommended={isRecommended(item.jobPoolItem)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <JobDetailModal job={detailJob} onClose={() => setDetailJob(null)} />

            {/* 求人プールからの削除の確認 (§6.2「求人プールからの削除（BTN-M-014）」手順 1・2・4、Issue #883)。
                既存の削除の確認 (活動詳細) と同じ表題・ボタン */}
            <ConfirmModal
                isOpen={deleteTarget !== null}
                title="削除の確認"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                confirmText={jobPoolDeleting ? '削除中...' : '削除する'}
                cancelText="キャンセル"
                confirmClassName="btn-delete"
                disabled={jobPoolDeleting}
            >
                {deleteTarget && (
                    <div className="job-pool-delete-confirm">
                        <p>
                            「{deleteTarget.company_name}／{deleteTarget.job_title}」を求人プールから削除します。
                            <strong>元に戻せません。</strong>
                        </p>
                        <p>
                            求人票のファイルと、この求人についてのおすすめポイントも削除されます。作成済みの推薦 PDF
                            は削除されません。
                        </p>
                        <p>
                            {/* ほかの案件の推薦結果にも入りうる (§6.2 手順 1、Issue #782) */}
                            この求人を含む推薦結果（ほかの案件の推薦結果を含む）では「この求人は求人プールにありません」と
                            表示され、これから出力する推薦 PDF には載りません。
                        </p>
                        {jobPoolDeleteError && (
                            <p className="job-pool-delete-error" role="alert">{jobPoolDeleteError}</p>
                        )}
                    </div>
                )}
            </ConfirmModal>
        </div>
    );
}

export default RecommendationList;
